import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { get, map } from 'lodash';
import queryString from 'query-string';
import { FormFinishInfo } from 'rc-field-form/lib/FormContext';
import AccountSetting from 'components/user/signup/SignupAccountSetting';
import AccountSettingWithType from 'components/user/signup/SignupAccountSettingWithType';
import AvailableClassSchedule from 'components/user/signup/SignupAvailableClassSchedule';
import Education from 'components/user/signup/SignupEducation';
import ProfileSetting from 'components/user/signup/SignupProfileSetting';
import SelectType from 'components/user/signup/SignupSelectType';
import Verification from 'components/user/signup/SignupVerification';
import WorkExperience from 'components/user/signup/SignupWorkExperience';
import { CSAUTHToken, CSTEMPUSERID } from 'libs/api/apiClient';
import {
  menteeCreate,
  menteeCreateKakao,
  mentorCreate,
  mentorCreateKakao,
  validatePassword,
} from 'libs/api/user';
import { filteredFileNames } from 'libs/utils';
import {
  TUserCreate,
  TMenteeCreate,
  TMentorCreate,
  TValidatePassword,
  TMenteeCreateKakao,
  TMentorCreateKakao,
} from 'modules/user/types';
import { EUserType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Steps, message, Form, Typography, Row, Col } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { PrimaryColor, SecondaryColor, device } from 'GlobalStyles';
import LogoSource from 'assets/images/logos/logo.svg';

const { Step } = Steps;
const { Text, Title } = Typography;
const StepWrapper = styled.div`
  height: 100%;

  .step-box {
    padding: 30px;
    border-right: 1px solid #e4e4e4;
    @media screen and ${device.md} {
      border-right: none;
    }
  }

  .ant-steps-item-title {
    font-size: 13px;
  }
  .steps-content {
    min-height: 200px;
    margin: 60px auto;
    max-width: 750px;
    padding: 0px 30px;
    @media screen and ${device.md} {
      margin: 10px auto;
    }
  }
  .steps-action {
    margin-top: 20px;
    text-align: center;
    .ant-btn {
      font-size: 14px;
      width: 100%;
      height: 45px;
    }
  }
  .ant-form-item {
    margin-bottom: 12px;
  }
  .ant-steps-item-icon {
    text-align: left;
    font-size: 12px;
    width: 20px;
    background: #fff !important;
    margin-right: 6px !important;
  }
  .ant-steps-item-custom .ant-steps-item-icon > .ant-steps-icon {
    font-size: 12px;
  }
  .ant-steps-item-description {
    padding-bottom: 0px !important;
  }
  .ant-steps-item-finish .ant-steps-icon,
  .ant-steps-item-process .ant-steps-icon {
    color: ${SecondaryColor} !important;
  }
  .ant-steps-item-tail {
    padding-top: 28px !important;
    padding-bottom: 0px !important;
    left: 6px !important;
  }
  .ant-steps-item-finish .ant-steps-item-title {
    color: ${SecondaryColor} !important;
    opacity: 0.5;
  }
  .ant-steps-item-process .ant-steps-item-title {
    color: ${SecondaryColor} !important;
  }
  .ant-steps-item-tail::after {
    border: 1px solid #cecdea;
  }
`;
const SignupStep = () => {
  const next = queryString.parse(useLocation().search)?.next?.toString();
  const [current, setCurrent] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const [data, setData] = useState<TUserCreate>();
  const [form] = Form.useForm();
  const [steps, setSteps] = useState([
    {
      title: '타입 선택하기',
      content: <SelectType form={form} />,
      index: 0,
    },
  ]);
  const search = useLocation().search;
  const signupType = new URLSearchParams(search).get('type'); // kakao or apple
  console.log(signupType);

  const menteeSteps = [
    {
      title: '계정 설정',
      content:
        signupType === 'kakao' || signupType === 'apple' ? (
          <AccountSettingWithType form={form} userType={EUserType.Mentee} />
        ) : (
          <AccountSetting form={form} userType={EUserType.Mentee} />
        ),
      index: 1,
    },
    {
      title: '프로필 설정',
      content: <ProfileSetting form={form} userType={EUserType.Mentee} />,
      index: 2,
    },
    {
      title: '학력사항 입력',
      content: (
        <Education
          form={form}
          userType={EUserType.Mentee}
          onClickNext={() => {
            onClickNextIndex(4);
          }}
        />
      ),
      index: 3,
    },
    {
      title: '경력사항 입력',
      content: (
        <WorkExperience
          form={form}
          userType={EUserType.Mentee}
          // onClickNext={() => {onClickNextIndex(5)}}
        />
      ),
      index: 4,
    },
    // {
    //   title: '인턴근무 가능일 설정',
    //   content: <AvailableWorkingDay form={form} userType={EUserType.Mentee}/>,
    //   index: 5,
    // },
  ];
  const mentorSteps = [
    {
      title: '계정 설정',
      content:
        signupType === 'kakao' || signupType === 'apple' ? (
          <AccountSettingWithType form={form} userType={EUserType.Mentor} />
        ) : (
          <AccountSetting form={form} userType={EUserType.Mentor} />
        ),
      index: 1,
    },
    {
      title: '전,현직 인증',
      content: (
        <Verification
          form={form}
          menteeId={window.localStorage.getItem(CSTEMPUSERID) ?? ''}
          userType={EUserType.Mentor}
          onFinish={() => {
            onClickNextIndex(3);
          }}
        />
      ),
      index: 2,
    },
    {
      title: '프로필 설정',
      content: <ProfileSetting form={form} userType={EUserType.Mentor} />,
      index: 3,
    },
    {
      title: '경력사항 입력',
      content: (
        <WorkExperience
          form={form}
          userType={EUserType.Mentor}
          onClickNext={() => {
            onClickNextIndex(5);
          }}
        />
      ),
      index: 4,
    },
    {
      title: '학력사항 입력',
      content: (
        <Education
          form={form}
          userType={EUserType.Mentor}
          onClickNext={() => {
            onClickNextIndex(6);
          }}
        />
      ),
      index: 5,
    },
    {
      title: '수업가능일 설정',
      content: <AvailableClassSchedule form={form} userType={EUserType.Mentor} />,
      index: 6,
    },
  ];

  useEffect(() => {
    if (current > maxIndex) {
      setMaxIndex(current);
    }
  }, [current]);

  function onClickNextIndex(index: number) {
    setCurrent(index);
  }
  function onClickNext() {
    setCurrent(current + 1);
  }

  function onClickPrev() {
    setCurrent(current - 1);
  }

  function onChange(target: number) {
    // 왼쪽 링크로 Step 바꾸기전에 error인 필드 있는지 체크
    const fieldErrorList = form.getFieldsError();
    let hasError = false;
    fieldErrorList &&
      map(fieldErrorList, (fieldError) => {
        if (get(fieldError, 'errors', []).length > 0) {
          hasError = true;
        }
      });
    if (hasError == false) {
      setCurrent(target);
    }
  }

  function isDisabled(index: number) {
    if (index > maxIndex) return true;

    return false;
  }

  //회원가입 스텝마다 완료 완료 클릭 때
  async function onFormFinish(formName: string, info?: FormFinishInfo) {
    switch (formName) {
      //멘티 멘토 회원가입의 타입 선택
      case 'select_type':
        let tempSteps = [];
        if (form.getFieldValue('user_type') === EUserType.Mentee) {
          tempSteps.push(steps[0], ...menteeSteps); //타입 선택 페이지 다음에 멘티 가입 프로세스
        } else {
          tempSteps.push(steps[0], ...mentorSteps); //타입 선택 페이지 다음에 멘토 가입 프로세스
        }
        setSteps(tempSteps);
        setData({
          ...form.getFieldsValue(),
        } as TUserCreate);
        onClickNext(); //데이터 저장하고 다음 스텝으로 넘어감
        form.resetFields();
        break;
      // 본인 인증
      case 'verification':
        break;
      //프로필 이미지, 이름, 닉네임 설정
      case 'profile_setting':
        const profile_image_ids = filteredFileNames(form.getFieldValue('profile_images'));
        if (profile_image_ids && profile_image_ids.length > 0 && profile_image_ids[0] != null) {
          setData({
            ...data,
            ...form.getFieldsValue(),
            profile_image_ids: filteredFileNames(form.getFieldValue('profile_images')),
          } as TUserCreate);
          onClickNext();
        } else if (get(data, 'profile_image_ids', []).length > 0) {
          onClickNext();
        } else {
          message.error('프로필 사진을 업로드 해주세요');
        }
        break;
      //계정 설정 (아이디, 비밀번호 )
      case 'account_setting':
        if (signupType === 'kakao' || signupType === 'apple') {
          // do nothing
        } else {
          try {
            const response = await validatePassword({
              password: form.getFieldValue('password'),
            } as TValidatePassword);
          } catch (e) {
            throw e;
          }
        }
        setData({
          ...data,
          ...form.getFieldsValue(),
        } as TUserCreate);
        onClickNext();
        break;
      //경력사항
      // case 'work_experience':
      case 'work_experience':
        //멘티일 경우에만
        if (data?.user_type === EUserType.Mentee) {
          try {
            if (signupType === 'kakao' || signupType === 'apple') {
              const response = await menteeCreateKakao({
                ...data,
                ...form.getFieldsValue(),
                resume_file_ids: filteredFileNames(form.getFieldValue('resume_files')),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                type: signupType,
                token: window.localStorage.getItem(CSAUTHToken),
                code: 'web',
                country_code: '82',
              } as TMenteeCreateKakao);
            } else {
              const response = await menteeCreate({
                ...data,
                ...form.getFieldsValue(),
                resume_file_ids: filteredFileNames(form.getFieldValue('resume_files')),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              } as TMenteeCreate);
            }
          } catch (e) {
            throw e;
          }
          window.location.href = next
            ? next
            : `${ERoute.UserSignupCompleted}?userType=${get(data, 'user_type')}`;
          form.resetFields();
        } else {
          setData({
            ...data,
            ...form.getFieldsValue(),
          } as TUserCreate);
          onClickNext();
        }
        break;
      case 'education': //학력사항
        setData({
          ...data,
          ...form.getFieldsValue(),
        } as TUserCreate);
        onClickNext();
        break;
      //인턴 근무 가능일
      // case 'available_working_day': // 멘티
      //   try {
      //     const response = await menteeCreate({
      //       ...data,
      //       ...form.getFieldsValue(),
      //       resume_file_ids: filteredFileNames(form.getFieldValue('resume_files')),
      //       timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      //     } as TMenteeCreate);
      //   } catch (e) {
      //     throw e;
      //   }
      //   window.location.href = `${ERoute.UserSignupCompleted}?userType=${get(data, 'user_type')}`;
      //   form.resetFields();
      //   break;
      //수업가능일 설정
      case 'available_class_schedule': // 멘토
        try {
          if (signupType === 'kakao' || signupType === 'apple') {
            const response = await mentorCreateKakao({
              ...data,
              ...form.getFieldsValue(),
              primary_field: get(data, 'mentoring_field'),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              type: signupType,
              token: window.localStorage.getItem(CSAUTHToken),
              code: 'web',
              country_code: '82',
            } as TMentorCreateKakao);
          } else {
            const response = await mentorCreate({
              ...data,
              ...form.getFieldsValue(),
              primary_field: get(data, 'mentoring_field'),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            } as TMentorCreate);
          }
        } catch (e) {
          throw e;
        }

        window.location.href = next
          ? next
          : `${ERoute.UserSignupCompleted}?userType=${get(data, 'user_type')}`;

        form.resetFields();
        break;
    }
  }

  return (
    <StepWrapper>
      <Row style={{ height: '100%' }}>
        <Col xs={24} sm={24} md={5} lg={5} xl={5} className='step-box'>
          <Link to='/'>
            <img
              alt='logo'
              src={LogoSource}
              style={{
                maxWidth: '200px',
                width: '100%',
                height: 'auto',
                marginBottom: '30px',
              }}
            />
          </Link>
          <Title level={3}>회원가입</Title>
          <Text strong>커리어 스텝에 오신것을 환영합니다</Text>
          <Steps current={current} onChange={onChange} direction='vertical' style={{ marginTop: '30px' }}>
            {steps.map((item) => (
              <Step
                key={item.title}
                title={item.title}
                description={<br />}
                disabled={isDisabled(item.index)}
                icon={<CheckCircleOutlined />}
              />
            ))}
          </Steps>
        </Col>
        <Col xs={24} sm={24} md={19} lg={19} xl={19}>
          <Form.Provider onFormFinish={onFormFinish}>
            <div className='steps-content'>{steps[current].content}</div>
          </Form.Provider>
        </Col>
      </Row>
    </StepWrapper>
  );
};

export default SignupStep;
